import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PhoneCountryInput from './PhoneCountryInput';
import { useLocale } from '../context/LocaleContext';
import { useBookAppointmentModal } from '../context/BookAppointmentModalContext';
import { fetchApi, getStoredUser } from '../utils/authClient';
import { parseInternationalPhone, validatePhoneLocal } from '../utils/phoneInput';
import './BookAppointmentModal.css';
import './PhoneCountryInput.css';

function splitFullName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

const EMPTY_FORM = {
  fullName: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneLocal: '',
  documentType: 'DNI',
  documentNumber: '',
  privacyAccepted: false,
  message: '',
};

export default function BookAppointmentModal() {
  const { t } = useLocale();
  const { open, closeBookAppointmentModal } = useBookAppointmentModal();
  const [phoneCountry, setPhoneCountry] = useState('PE');
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [clauseOpen, setClauseOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const resetFormFromUser = useCallback(() => {
    const u = getStoredUser();
    if (u) {
      const { firstName, lastName } = splitFullName(u.name);
      const parsed = parseInternationalPhone(u.phone);
      setPhoneCountry(parsed.countryCode);
      setForm({
        fullName: `${firstName === '—' ? '' : firstName} ${lastName || ''}`.trim(),
        firstName: firstName === '—' ? '' : firstName,
        lastName: lastName || '',
        email: String(u.email || '').trim(),
        phoneLocal: parsed.local,
        documentType: 'DNI',
        documentNumber: '',
        privacyAccepted: false,
        message: '',
      });
    } else {
      setPhoneCountry('PE');
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, []);

  useEffect(() => {
    if (!open) return;
    resetFormFromUser();
    setClauseOpen(false);
    setSubmitting(false);
    setSubmitStatus(null);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') closeBookAppointmentModal();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closeBookAppointmentModal, resetFormFromUser]);

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setSubmitStatus(null);
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const updateFullName = (value) => {
    const parts = String(value || '')
      .trimStart()
      .split(/\s+/)
      .filter(Boolean);
    setSubmitStatus(null);
    setForm((f) => ({
      ...f,
      fullName: value,
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' '),
    }));
    setErrors((prev) => {
      if (!prev.fullName && !prev.firstName && !prev.lastName) return prev;
      const next = { ...prev };
      delete next.fullName;
      delete next.firstName;
      delete next.lastName;
      return next;
    });
  };

  const validate = () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const fullName = String(form.fullName || `${firstName} ${lastName}`).trim();
    const email = form.email.trim();
    const phoneCheck = validatePhoneLocal(phoneCountry, form.phoneLocal);
    const nextErrors = {};

    if (!fullName || !firstName || !lastName) nextErrors.fullName = t('booking.fieldRequired');
    if (!email) nextErrors.email = t('booking.fieldRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = t('booking.emailInvalid');
    }
    if (!phoneCheck.ok) {
      if (phoneCheck.reason === 'empty') nextErrors.phone = t('booking.fieldRequired');
      else if (phoneCheck.reason === 'short') nextErrors.phone = t('auth.phoneTooShort');
      else nextErrors.phone = t('auth.phoneInvalid');
    }
    if (!String(form.documentNumber || '').trim()) {
      nextErrors.documentNumber = t('booking.fieldRequired');
    }
    if (!form.privacyAccepted) {
      nextErrors.privacyAccepted = t('booking.privacyRequired');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    const phoneCheck = validatePhoneLocal(phoneCountry, form.phoneLocal);
    const fullName = String(form.fullName || `${form.firstName} ${form.lastName}`).trim();

    setSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetchApi('/api/booking-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName,
          phone: phoneCheck.ok ? phoneCheck.phone : form.phoneLocal,
          email: form.email.trim(),
          documentType: form.documentType,
          documentNumber: form.documentNumber.trim(),
          privacyAccepted: Boolean(form.privacyAccepted),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === false) {
        setSubmitStatus({
          type: 'error',
          message: data.message || t('booking.emailSendError'),
        });
        return;
      }

      setSubmitStatus({ type: 'success', message: t('booking.emailSendSuccess') });
      window.setTimeout(() => {
        closeBookAppointmentModal();
        setClauseOpen(false);
      }, 1200);
    } catch {
      setSubmitStatus({ type: 'error', message: t('booking.emailSendError') });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="book_appt_overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeBookAppointmentModal();
      }}
    >
      <div
        className="book_appt_card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-appt-title"
        onClick={(e) => e.stopPropagation()}
      >
        <aside className="book_appt_aside">
          <h2 id="book-appt-title" className="book_appt_title">
            {t('booking.confirmTitle')}
          </h2>
          <p className="book_appt_subtitle">{t('booking.confirmCardIntro')}</p>
          <div className="book_appt_note">
            <i className="fa fa-info-circle" aria-hidden="true" />
            <span>{t('booking.confirmArrivalNote')}</span>
          </div>
        </aside>

        <form className="book_appt_form" onSubmit={handleSubmit} noValidate>
          <button
            type="button"
            className="book_appt_close"
            onClick={closeBookAppointmentModal}
            aria-label={t('booking.bookModalClose')}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>

          <div className="book_appt_fields">
            <label className={`book_appt_field book_appt_field--full${errors.fullName ? ' book_appt_field--error' : ''}`}>
              <span>{t('booking.labelFullName')}</span>
              <input
                type="text"
                autoComplete="name"
                value={form.fullName}
                onChange={(e) => updateFullName(e.target.value)}
              />
              {errors.fullName ? (
                <span className="book_appt_error" role="alert">
                  {errors.fullName}
                </span>
              ) : null}
            </label>

            <div
              className={`book_appt_field book_appt_field--full${errors.phone ? ' book_appt_field--error' : ''}`}
            >
              <span>{t('booking.labelMobilePhone')}</span>
              <PhoneCountryInput
                compact
                countryCode={phoneCountry}
                onCountryCodeChange={setPhoneCountry}
                local={form.phoneLocal}
                onLocalChange={(digits) => updateField('phoneLocal', digits)}
              />
              {errors.phone ? (
                <span className="book_appt_error" role="alert">
                  {errors.phone}
                </span>
              ) : null}
            </div>

            <label className={`book_appt_field book_appt_field--full${errors.email ? ' book_appt_field--error' : ''}`}>
              <span>{t('booking.labelEmail')}</span>
              <input
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
              />
              {errors.email ? (
                <span className="book_appt_error" role="alert">
                  {errors.email}
                </span>
              ) : null}
            </label>

            <label className="book_appt_field">
              <span>{t('booking.labelDocumentType')}</span>
              <select
                value={form.documentType}
                onChange={(e) => updateField('documentType', e.target.value)}
              >
                <option value="DNI">DNI</option>
                <option value="CE">CE</option>
                <option value="Pasaporte">Pasaporte</option>
              </select>
            </label>

            <label className={`book_appt_field${errors.documentNumber ? ' book_appt_field--error' : ''}`}>
              <span>{t('booking.labelDocumentNumber')}</span>
              <input
                type="text"
                inputMode="numeric"
                value={form.documentNumber}
                onChange={(e) => updateField('documentNumber', e.target.value)}
              />
              {errors.documentNumber ? (
                <span className="book_appt_error" role="alert">
                  {errors.documentNumber}
                </span>
              ) : null}
            </label>

            <div
              className={`book_appt_privacy_wrap book_appt_field--full${
                errors.privacyAccepted ? ' book_appt_privacy--error' : ''
              }`}
            >
              <label className="book_appt_privacy">
                <input
                  type="checkbox"
                  checked={Boolean(form.privacyAccepted)}
                  onChange={(e) => updateField('privacyAccepted', e.target.checked)}
                />
                <span>{t('booking.privacyAcceptedPrefix')}</span>
              </label>
              <button
                type="button"
                className="book_appt_clause_link"
                onClick={() => setClauseOpen(true)}
              >
                {t('booking.privacyClauseTitle')}
              </button>
            </div>
            {errors.privacyAccepted ? (
              <span className="book_appt_error book_appt_field--full" role="alert">
                {errors.privacyAccepted}
              </span>
            ) : null}
            {submitStatus ? (
              <span
                className={`book_appt_submit_status book_appt_submit_status--${submitStatus.type} book_appt_field--full`}
                role="status"
              >
                {submitStatus.message}
              </span>
            ) : null}
          </div>

          <button
            type="submit"
            className="book_appt_btn book_appt_btn--primary"
            disabled={submitting || submitStatus?.type === 'success'}
          >
            {submitting ? t('booking.sending') : t('booking.submitReservation')}
          </button>
        </form>
      </div>

      {clauseOpen ? (
        <div
          className="book_appt_clause_overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setClauseOpen(false);
          }}
        >
          <div
            className="book_appt_clause_modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="book-appt-clause-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="book_appt_clause_head">
              <h3 id="book-appt-clause-title">{t('booking.privacyClauseTitle')}</h3>
              <button
                type="button"
                className="book_appt_clause_close"
                onClick={() => setClauseOpen(false)}
                aria-label={t('booking.bookModalClose')}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
            <p>{t('booking.privacyClauseBody')}</p>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
}
